import React, { useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  ButtonBase,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  BugReportOutlined,
  ChatBubbleOutlineRounded,
  CheckCircleOutlineRounded,
  CloseRounded,
  CloudUploadOutlined,
  DeleteOutlineRounded,
  ErrorOutlineRounded,
  FeedbackOutlined,
  InsertDriveFileOutlined,
  LockOutlined,
  SendRounded,
} from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import BackdropOverlay from "../common/BackdropOverlay";
import {
  DIALOG_ACCEPT_BUTTON_SX,
  DIALOG_CANCEL_BUTTON_SX,
} from "../common/dialogActionButtonSx";
import { OUTLINED_INPUT_SX } from "../common/outlinedInputStyles";
import TooltipComponent from "../common/Tooltip";
import { useScrollLock } from "../common/lenisContext";
import { colors } from "../../theme/colors";
import {
  resolveUiDesignLane,
  submitUiExperimentFeedback,
} from "../../universal/uiExperimentFeedback";
import {
  MOBILE_NAV_FAB_BOTTOM,
  MOBILE_NAV_MEDIA,
} from "../layout/layoutConstants";

const FEEDBACK_TYPES = [
  {
    value: "bug",
    label: "Bug report",
    description: "Something is not working",
    icon: BugReportOutlined,
  },
  {
    value: "feedback",
    label: "UI feedback",
    description: "Share an idea or improvement",
    icon: ChatBubbleOutlineRounded,
  },
];

const SECTION_LABEL_SX = {
  color: alpha(colors.accent.main, 0.82),
  fontSize: "0.65rem",
  fontWeight: 700,
  letterSpacing: "0.16em",
  lineHeight: 1,
  textTransform: "uppercase",
};

export default function UiFeedbackFab({ user }) {
  const location = useLocation();
  const isMobileNav = useMediaQuery(MOBILE_NAV_MEDIA);
  const attachmentInputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("bug");
  const [comment, setComment] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useScrollLock(open);

  const pagePath = location.pathname;
  const designLane = useMemo(() => resolveUiDesignLane(pagePath), [pagePath]);

  function clearAttachment() {
    setAttachment(null);
    if (attachmentInputRef.current) {
      attachmentInputRef.current.value = "";
    }
  }

  function resetForm() {
    setKind("bug");
    setComment("");
    clearAttachment();
    setError("");
    setSuccess("");
  }

  function handleClose() {
    setOpen(false);
    resetForm();
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError("");
    setSuccess("");

    const trimmedComment = comment.trim();
    if (!trimmedComment) {
      setError("Please add a short description.");
      return;
    }

    setSubmitting(true);
    try {
      await submitUiExperimentFeedback({
        kind,
        comment: trimmedComment,
        pagePath,
        designLane,
        nickname: user?.nickname || user?.name || null,
        attachment,
      });
      setSuccess("Feedback sent. Thank you for helping us improve Ouro.");
      setComment("");
      clearAttachment();
    } catch (submitError) {
      setError(submitError?.message || "Could not send feedback.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <TooltipComponent title="UI feedbacks and Bug reports" placement="left">
        <ButtonBase
          aria-label="UI feedbacks and Bug reports"
          onClick={() => setOpen(true)}
          sx={{
            position: "fixed",
            right: { xs: "1rem", md: "1.25rem" },
            bottom: isMobileNav
              ? MOBILE_NAV_FAB_BOTTOM
              : { xs: "1rem", md: "1.25rem" },
            zIndex: 1350,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "2.75rem",
            height: "2.75rem",
            minWidth: "2.75rem",
            minHeight: "2.75rem",
            p: 0,
            borderRadius: "999px",
            color: colors.text.contrast,
            background: colors.gradient.accent,
            border: `1px solid ${colors.border.default}`,
            boxShadow: "0 14px 34px rgba(0,0,0,0.42)",
            transition: "border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
            "&:hover": {
              boxShadow: "0 18px 40px rgba(0,0,0,0.48)",
              borderColor: colors.primary.hover,
            },
          }}
        >
          <FeedbackOutlined sx={{ fontSize: "1.1rem" }} />
        </ButtonBase>
      </TooltipComponent>

      {open ? (
        <BackdropOverlay
          open
          onClick={handleClose}
          sx={{
            height: "100dvh",
            minHeight: "100dvh",
            boxSizing: "border-box",
            overflow: "hidden",
            backgroundColor: "rgba(3, 3, 5, 0.72)",
            backgroundImage: `
              radial-gradient(circle at 50% 18%, ${alpha(colors.primary.main, 0.09)}, transparent 38%),
              linear-gradient(rgba(0,0,0,0.08), rgba(0,0,0,0.26))
            `,
          }}
          contentSx={{
            display: "flex",
            justifyContent: "center",
            width: "100%",
            minHeight: 0,
          }}
        >
          <Box
            component="form"
            onSubmit={handleSubmit}
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="ui-feedback-dialog-title"
            sx={{
              "@keyframes feedbackDialogEnter": {
                from: { opacity: 0, transform: "translateY(10px) scale(0.985)" },
                to: { opacity: 1, transform: "translateY(0) scale(1)" },
              },
              position: "relative",
              display: "flex",
              flexDirection: "column",
              width: { xs: "calc(100vw - 2rem)", sm: "38rem" },
              maxWidth: "38rem",
              maxHeight: {
                xs: "calc(100dvh - 2rem - env(safe-area-inset-bottom, 0px))",
                sm: "calc(100dvh - 4rem)",
              },
              overflow: "hidden",
              color: colors.text.primary,
              background: `
                radial-gradient(circle at 8% 0%, ${alpha(colors.primary.main, 0.11)}, transparent 34%),
                linear-gradient(155deg, rgba(19,17,15,0.995) 0%, rgba(10,10,12,0.995) 44%, rgba(7,7,9,1) 100%)
              `,
              borderRadius: { xs: "14px", sm: "16px" },
              border: `1px solid ${alpha(colors.accent.main, 0.17)}`,
              boxShadow: `
                0 34px 90px rgba(0, 0, 0, 0.68),
                0 0 0 1px rgba(255,255,255,0.025) inset,
                0 0 42px ${alpha(colors.primary.main, 0.045)}
              `,
              animation: "feedbackDialogEnter 220ms cubic-bezier(0.16, 1, 0.3, 1)",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: "8%",
                right: "8%",
                height: "1px",
                background: `linear-gradient(90deg, transparent, ${alpha(colors.accent.soft, 0.72)}, transparent)`,
                pointerEvents: "none",
              },
            }}
          >
            <IconButton
              onClick={handleClose}
              aria-label="Close feedback dialog"
              sx={{
                position: "absolute",
                zIndex: 2,
                top: { xs: "0.85rem", sm: "1rem" },
                right: { xs: "0.85rem", sm: "1rem" },
                width: "2rem",
                height: "2rem",
                borderRadius: "8px",
                color: colors.text.secondary,
                backgroundColor: alpha("#fff", 0.035),
                border: `1px solid ${alpha("#fff", 0.045)}`,
                transition: "color 150ms ease, background-color 150ms ease, border-color 150ms ease",
                "&:hover": {
                  color: colors.text.primary,
                  backgroundColor: alpha("#fff", 0.075),
                  borderColor: alpha("#fff", 0.085),
                },
              }}
            >
              <CloseRounded sx={{ fontSize: "1.1rem" }} />
            </IconButton>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "0.9rem",
                px: { xs: "1.15rem", sm: "1.6rem" },
                pt: { xs: "1.2rem", sm: "1.45rem" },
                pb: { xs: "1.1rem", sm: "1.3rem" },
                pr: { xs: "3.5rem", sm: "4rem" },
                flexShrink: 0,
                borderBottom: `1px solid ${colors.border.subtle}`,
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  display: "grid",
                  placeItems: "center",
                  width: { xs: "2.55rem", sm: "2.85rem" },
                  height: { xs: "2.55rem", sm: "2.85rem" },
                  flexShrink: 0,
                  borderRadius: "11px",
                  color: colors.accent.main,
                  background: `linear-gradient(145deg, ${alpha(colors.primary.main, 0.18)}, ${alpha(colors.primary.main, 0.055)})`,
                  border: `1px solid ${alpha(colors.accent.main, 0.22)}`,
                  boxShadow: `0 10px 26px ${alpha(colors.primary.main, 0.08)}, inset 0 1px 0 ${alpha("#fff", 0.06)}`,
                }}
              >
                <FeedbackOutlined sx={{ fontSize: { xs: "1.18rem", sm: "1.3rem" } }} />
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ ...SECTION_LABEL_SX, mb: "0.4rem" }}>
                  Ouro support
                </Typography>
                <Typography
                  id="ui-feedback-dialog-title"
                  sx={{
                    fontSize: { xs: "1.12rem", sm: "1.3rem" },
                    fontWeight: 700,
                    letterSpacing: "-0.015em",
                    lineHeight: 1.15,
                  }}
                >
                  Share UI feedback
                </Typography>
              </Box>
            </Box>

            <Box
              data-lenis-prevent
              data-lenis-prevent-wheel
              data-lenis-prevent-touch
              sx={{
                minHeight: 0,
                flex: "1 1 auto",
                overflowX: "hidden",
                overflowY: "auto",
                overscrollBehavior: "contain",
                WebkitOverflowScrolling: "touch",
                touchAction: "pan-y",
                scrollbarWidth: "thin",
                scrollbarColor: `${alpha(colors.accent.main, 0.38)} transparent`,
                px: { xs: "1.15rem", sm: "1.6rem" },
                pt: { xs: "1.1rem", sm: "1.35rem" },
                pb: { xs: "1rem", sm: "1.25rem" },
                "&::-webkit-scrollbar": {
                  width: "4px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  borderRadius: "999px",
                  backgroundColor: alpha(colors.accent.main, 0.32),
                },
              }}
            >
              <Typography
                sx={{
                  maxWidth: "31rem",
                  color: colors.text.secondary,
                  fontSize: { xs: "0.81rem", sm: "0.86rem" },
                  lineHeight: 1.55,
                  mb: "1.2rem",
                }}
              >
                Found something that feels off? Send us the details and we’ll use them to improve the experience.
              </Typography>

              <Typography sx={{ ...SECTION_LABEL_SX, mb: "0.65rem" }}>
                Report type
              </Typography>
              <Box
                role="group"
                aria-label="Feedback type"
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                  gap: "0.65rem",
                  mb: "1.25rem",
                }}
              >
                {FEEDBACK_TYPES.map((type) => {
                  const selected = kind === type.value;
                  const TypeIcon = type.icon;

                  return (
                    <ButtonBase
                      key={type.value}
                      onClick={() => {
                        setKind(type.value);
                        setError("");
                        setSuccess("");
                      }}
                      aria-pressed={selected}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        gap: "0.7rem",
                        minWidth: 0,
                        px: "0.8rem",
                        py: "0.72rem",
                        textAlign: "left",
                        borderRadius: "10px",
                        color: selected ? colors.text.primary : colors.text.secondary,
                        background: selected
                          ? `linear-gradient(145deg, ${alpha(colors.primary.main, 0.14)}, ${alpha(colors.primary.main, 0.045)})`
                          : alpha(colors.background.surface, 0.78),
                        border: `1px solid ${selected ? alpha(colors.accent.main, 0.34) : colors.border.default}`,
                        boxShadow: selected
                          ? `inset 0 1px 0 ${alpha("#fff", 0.045)}, 0 8px 22px ${alpha(colors.primary.main, 0.06)}`
                          : "inset 0 1px 0 rgba(255,255,255,0.018)",
                        transition: "border-color 150ms ease, background 150ms ease, color 150ms ease, transform 150ms ease",
                        "&:hover": {
                          borderColor: alpha(colors.accent.main, selected ? 0.44 : 0.2),
                          background: selected
                            ? `linear-gradient(145deg, ${alpha(colors.primary.main, 0.17)}, ${alpha(colors.primary.main, 0.055)})`
                            : alpha("#fff", 0.035),
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "grid",
                          placeItems: "center",
                          width: "2rem",
                          height: "2rem",
                          flexShrink: 0,
                          borderRadius: "8px",
                          color: selected ? colors.accent.main : colors.text.muted,
                          backgroundColor: selected
                            ? alpha(colors.primary.main, 0.12)
                            : alpha("#fff", 0.035),
                          border: `1px solid ${selected ? alpha(colors.accent.main, 0.18) : colors.border.subtle}`,
                        }}
                      >
                        <TypeIcon sx={{ fontSize: "1rem" }} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            color: "inherit",
                            fontSize: "0.8rem",
                            fontWeight: 650,
                            lineHeight: 1.2,
                          }}
                        >
                          {type.label}
                        </Typography>
                        <Typography
                          sx={{
                            mt: "0.2rem",
                            color: colors.text.muted,
                            fontSize: "0.68rem",
                            lineHeight: 1.2,
                          }}
                        >
                          {type.description}
                        </Typography>
                      </Box>
                    </ButtonBase>
                  );
                })}
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "space-between",
                  gap: 1,
                  mb: "0.65rem",
                }}
              >
                <Typography sx={SECTION_LABEL_SX}>Details</Typography>
                <Typography sx={{ color: colors.text.muted, fontSize: "0.67rem" }}>
                  Be as specific as possible
                </Typography>
              </Box>
              <TextField
                multiline
                minRows={4}
                fullWidth
                placeholder={
                  kind === "bug"
                    ? "What happened, and what did you expect?"
                    : "What would make the experience better?"
                }
                value={comment}
                onChange={(event) => {
                  setComment(event.target.value);
                  if (error) setError("");
                  if (success) setSuccess("");
                }}
                sx={{
                  ...OUTLINED_INPUT_SX,
                  mb: "1.2rem",
                  "& .MuiOutlinedInput-root": {
                    ...OUTLINED_INPUT_SX["& .MuiOutlinedInput-root"],
                    alignItems: "flex-start",
                    minHeight: "7.5rem",
                    borderRadius: "11px",
                    background: alpha(colors.background.surface, 0.8),
                  },
                  "& .MuiOutlinedInput-input": {
                    ...OUTLINED_INPUT_SX["& .MuiOutlinedInput-input"],
                    p: "0.8rem 0.9rem",
                    fontSize: "0.82rem",
                    lineHeight: 1.55,
                  },
                }}
              />

              <Typography sx={{ ...SECTION_LABEL_SX, mb: "0.65rem" }}>
                Evidence <Box component="span" sx={{ color: colors.text.muted }}>(optional)</Box>
              </Typography>
              {attachment ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    minWidth: 0,
                    p: "0.7rem 0.75rem",
                    borderRadius: "10px",
                    background: alpha(colors.primary.main, 0.055),
                    border: `1px solid ${alpha(colors.accent.main, 0.2)}`,
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      placeItems: "center",
                      width: "2.15rem",
                      height: "2.15rem",
                      flexShrink: 0,
                      borderRadius: "8px",
                      color: colors.accent.main,
                      backgroundColor: alpha(colors.primary.main, 0.12),
                      border: `1px solid ${alpha(colors.accent.main, 0.15)}`,
                    }}
                  >
                    <InsertDriveFileOutlined sx={{ fontSize: "1rem" }} />
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      title={attachment.name}
                      sx={{
                        overflow: "hidden",
                        color: colors.text.primary,
                        fontSize: "0.78rem",
                        fontWeight: 600,
                        lineHeight: 1.3,
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {attachment.name}
                    </Typography>
                    <Typography sx={{ mt: "0.15rem", color: colors.text.muted, fontSize: "0.67rem" }}>
                      Ready to attach
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={clearAttachment}
                    aria-label="Remove attachment"
                    sx={{
                      width: "2rem",
                      height: "2rem",
                      flexShrink: 0,
                      borderRadius: "8px",
                      color: colors.text.muted,
                      "&:hover": {
                        color: colors.text.primary,
                        backgroundColor: alpha("#fff", 0.055),
                      },
                    }}
                  >
                    <DeleteOutlineRounded sx={{ fontSize: "1rem" }} />
                  </IconButton>
                </Box>
              ) : (
                <ButtonBase
                  component="label"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: "0.75rem",
                    width: "100%",
                    p: "0.7rem 0.75rem",
                    textAlign: "left",
                    borderRadius: "10px",
                    background: alpha(colors.background.surface, 0.64),
                    border: `1px dashed ${alpha(colors.accent.main, 0.24)}`,
                    transition: "border-color 150ms ease, background-color 150ms ease",
                    "&:hover": {
                      backgroundColor: alpha(colors.primary.main, 0.055),
                      borderColor: alpha(colors.accent.main, 0.42),
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      placeItems: "center",
                      width: "2.15rem",
                      height: "2.15rem",
                      flexShrink: 0,
                      borderRadius: "8px",
                      color: colors.accent.main,
                      backgroundColor: alpha(colors.primary.main, 0.1),
                      border: `1px solid ${alpha(colors.accent.main, 0.14)}`,
                    }}
                  >
                    <CloudUploadOutlined sx={{ fontSize: "1.05rem" }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        color: colors.text.primary,
                        fontSize: "0.78rem",
                        fontWeight: 600,
                        lineHeight: 1.3,
                      }}
                    >
                      Add screenshot or recording
                    </Typography>
                    <Typography sx={{ mt: "0.15rem", color: colors.text.muted, fontSize: "0.67rem" }}>
                      Image or video files
                    </Typography>
                  </Box>
                  <input
                    ref={attachmentInputRef}
                    hidden
                    type="file"
                    accept="image/*,video/*"
                    onChange={(event) => {
                      setAttachment(event.target.files?.[0] || null);
                      setError("");
                      setSuccess("");
                    }}
                  />
                </ButtonBase>
              )}

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.55rem",
                  minWidth: 0,
                  mt: "0.8rem",
                  px: "0.7rem",
                  py: "0.52rem",
                  borderRadius: "8px",
                  color: colors.text.muted,
                  backgroundColor: alpha("#fff", 0.018),
                  border: `1px solid ${colors.border.subtle}`,
                }}
              >
                <LockOutlined sx={{ flexShrink: 0, fontSize: "0.82rem", color: alpha(colors.accent.main, 0.55) }} />
                <Typography
                  title={pagePath}
                  sx={{
                    minWidth: 0,
                    flex: 1,
                    overflow: "hidden",
                    fontSize: "0.65rem",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  Page context included · {pagePath}
                </Typography>
                <Typography
                  sx={{
                    flexShrink: 0,
                    color: alpha(colors.text.secondary, 0.7),
                    fontSize: "0.61rem",
                    fontWeight: 700,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                  }}
                >
                  {designLane}
                </Typography>
              </Box>

              {error || success ? (
                <Box
                  role="status"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.55rem",
                    mt: "0.8rem",
                    px: "0.72rem",
                    py: "0.58rem",
                    borderRadius: "8px",
                    color: error ? "#F1A3A3" : "#75D99A",
                    backgroundColor: error
                      ? alpha(colors.state.danger.main, 0.09)
                      : alpha(colors.state.success.main, 0.08),
                    border: `1px solid ${error ? alpha(colors.state.danger.main, 0.18) : alpha(colors.state.success.main, 0.16)}`,
                  }}
                >
                  {error ? (
                    <ErrorOutlineRounded sx={{ flexShrink: 0, fontSize: "0.95rem" }} />
                  ) : (
                    <CheckCircleOutlineRounded sx={{ flexShrink: 0, fontSize: "0.95rem" }} />
                  )}
                  <Typography sx={{ color: "inherit", fontSize: "0.72rem", lineHeight: 1.35 }}>
                    {error || success}
                  </Typography>
                </Box>
              ) : null}
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "0.65rem",
                px: { xs: "1.15rem", sm: "1.6rem" },
                py: { xs: "0.9rem", sm: "1rem" },
                flexShrink: 0,
                backgroundColor: alpha("#000", 0.18),
                borderTop: `1px solid ${colors.border.subtle}`,
              }}
            >
              <Button
                onClick={handleClose}
                disabled={submitting}
                sx={{
                  ...DIALOG_CANCEL_BUTTON_SX,
                  minWidth: { xs: 0, sm: "7.75rem" },
                  height: "2.5rem",
                  px: { xs: "1rem", sm: "1.25rem" },
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disableElevation
                disabled={submitting}
                endIcon={submitting ? null : <SendRounded sx={{ fontSize: "0.9rem !important" }} />}
                sx={{
                  ...DIALOG_ACCEPT_BUTTON_SX,
                  minWidth: { xs: "9.5rem", sm: "10.75rem" },
                  height: "2.5rem",
                  px: { xs: "1rem", sm: "1.25rem" },
                  fontSize: "0.72rem",
                  letterSpacing: "0.075em",
                  textTransform: "uppercase",
                }}
              >
                {submitting ? "Sending..." : "Send feedback"}
              </Button>
            </Box>
          </Box>
        </BackdropOverlay>
      ) : null}
    </>
  );
}

UiFeedbackFab.propTypes = {
  user: PropTypes.object,
};
